"use client";

import * as React from "react";
import { usePathname } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";

import {
  SidebarMenuButton,
  SidebarMenuCollapsible,
  SidebarMenuCollapsibleContent,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/animate-ui/components/radix/sidebar";
import LottieIcon, {
  type LottieColorFilter,
} from "@/components/ui/lottie-icon";
import { prefetchBillingRoute } from "@/lib/client/prefetch-app-data";
import {
  EXPLORE_NAV_ITEMS,
  isExploreNavItemActive,
  isExploreNavPath,
  type ExploreNavItem,
} from "@/lib/platform/explore-nav";
import { EXPLORE_PARENT_ICON_PX } from "@/lib/platform/explore-nav-icon-sizes";
import { AUTHENTICATED_PRICING_PATH } from "@/lib/platform/pricing-destination";
import lottieFolder from "@/public/icons8-folder.json";

const EXPLORE_ICON_COLOR = "#7B68EE";
const EXPLORE_LOTTIE_FILTERS: LottieColorFilter[] = [
  { keypath: "**", color: EXPLORE_ICON_COLOR },
];

function ExploreSubNavRow({
  item,
  active,
  onPrefetch,
}: {
  item: ExploreNavItem;
  active: boolean;
  onPrefetch?: () => void;
}) {
  return (
    <SidebarMenuSubButton
      href={item.href}
      isActive={active}
      size="md"
      onMouseEnter={onPrefetch}
    >
      <span className="type-sidebar-item">{item.title}</span>
    </SidebarMenuSubButton>
  );
}

export function SidebarExploreNav() {
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const onExploreRoute = isExploreNavPath(pathname);
  const [hover, setHover] = React.useState(false);
  const [open, setOpen] = React.useState(onExploreRoute);

  React.useEffect(() => {
    if (onExploreRoute) setOpen(true);
  }, [onExploreRoute]);

  return (
    <SidebarMenuItem>
      <SidebarMenuCollapsible
        open={open}
        onOpenChange={setOpen}
        className="group/collapsible w-full"
      >
        <SidebarMenuButton
          isActive={onExploreRoute}
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <LottieIcon
            animationData={lottieFolder}
            size={EXPLORE_PARENT_ICON_PX}
            iconColor={EXPLORE_ICON_COLOR}
            colorFilters={EXPLORE_LOTTIE_FILTERS}
            speed={1.05}
            loopOnHover={false}
            parentHovered={hover}
          />
          <span className="type-sidebar-item">Explore</span>
          <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
        </SidebarMenuButton>

        <SidebarMenuCollapsibleContent>
          <SidebarMenuSub>
            {EXPLORE_NAV_ITEMS.map((item) => {
              const active = isExploreNavItemActive(pathname, item.href);
              return (
                <SidebarMenuSubItem key={item.href}>
                  <ExploreSubNavRow
                    item={item}
                    active={active}
                    onPrefetch={
                      item.href === AUTHENTICATED_PRICING_PATH
                        ? () => prefetchBillingRoute(queryClient)
                        : undefined
                    }
                  />
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </SidebarMenuCollapsibleContent>
      </SidebarMenuCollapsible>
    </SidebarMenuItem>
  );
}
